import React, { useContext } from "react";

import { CheckCircleFilled, CloseOutlined } from "@ant-design/icons";
import { Button, Modal } from "antd";
import moment from "moment";

import "./SampleSubmissionConfirmModal.css";

import SampleSubmissionContext from "../SampleSubmissionContext";
import Comment from "./Comment";
import SampleSubmissionModalEditsInfo from "./SampleSubmissionModalEditsInfo";

import { getTodayDateKey } from "../../Common/DatePicker/utils";

export default function SampleSubmissionConfirmModal({
  headerText,
  bodyText,
  buttonText,
  buttonFunctions,
  loading,
  editedSamplePayload,
  sampleFields,
  sampleFieldsInfo,
  sampleIdFields,
  submissionComment,
  commentRef,
  commentDefaultValue,
  handleEditSample,
  submissionEditingSample,
  onPrimaryAction,
  open = true,
  variant = "default",
}) {
  const { thirdParty, isLoggedOut } = useContext(SampleSubmissionContext);
  const isIssueScheduleVariant = variant === "issueSchedule";
  const todayKey = getTodayDateKey();
  const [selectedScheduleDateKey, setSelectedScheduleDateKey] = React.useState(
    isIssueScheduleVariant ? null : todayKey,
  );
  const scheduleDateOptions = Array.from({ length: 5 }, (_, i) => {
    const date = moment().add(i, "days");
    return {
      key: date.format("YYYY-M-D"),
      month: date.format("MMM"),
      day: date.format("D"),
      isToday: i === 0,
    };
  });

  const isNoChangesConfirmation = Boolean(editedSamplePayload?.noChanges);
  const isIssueFlowConfirmation = Boolean(submissionEditingSample?.issueFlow);
  const isIssueFlowModal = isIssueFlowConfirmation;
  const showCloseIcon = !isIssueFlowModal && (
    isIssueScheduleVariant || isNoChangesConfirmation || isIssueFlowConfirmation
  );

  const handleCancel = () => {
    if (submissionEditingSample?.issueFlow && typeof handleEditSample === "function") {
      handleEditSample(0);
      buttonFunctions[0]();
      return;
    }
    if (submissionEditingSample && typeof handleEditSample === "function" && !isIssueScheduleVariant) {
      handleEditSample(0);
    }
    buttonFunctions[0]();
  };

  let modalWidth = "391px";
  if (submissionComment || isIssueScheduleVariant) {
    modalWidth = "520px";
  }
  if (editedSamplePayload && !isNoChangesConfirmation) {
    modalWidth = "800px";
  }

  let primaryBtnLabel = buttonText[1];
  if (submissionComment) {
    primaryBtnLabel = "Submit";
  }

  const handlePrimaryClick = () => {
    if (typeof onPrimaryAction === "function") {
      onPrimaryAction(selectedScheduleDateKey);
      return;
    }
    buttonFunctions[1]();
  };

  const renderScheduleDates = () => (
    <div className="sampleSubmissionSchedule">
      <div className="sampleSubmissionSchedule__Title">When would you like submit this submission?</div>
      <div className="sampleSubmissionSchedule__Row">
        <div className="sampleSubmissionSchedule__Dates">
          {scheduleDateOptions.map((item) => (
            <div key={item.key} className="sampleSubmissionSchedule__DateItem">
              <button
                type="button"
                className={`sampleSubmissionSchedule__DatePill ${selectedScheduleDateKey === item.key ? "sampleSubmissionSchedule__DatePill--active" : ""}`}
                onClick={() => setSelectedScheduleDateKey(item.key)}
              >
                <span>{item.month}</span>
                <span>{item.day}</span>
              </button>
              {item.isToday && (
                <span className="sampleSubmissionSchedule__TodayLabel">Today</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const isDeleteHeader = headerText === "Delete Sample"
    || headerText === "Delete Sample Confirmation"
    || headerText === "Delete Draft Confirmation";

  const renderModalBody = () => {
    if (isIssueScheduleVariant) {
      return (
        <>
          <div className="sampleSubmissionConfirmDel__addHeader">
            <div className="sampleSubmissionConfirmDelTitle sampleSubmissionConfirmDelTitle--add">{headerText}</div>
            <div className="sampleSubmissionConfirmDelSubtitle">{bodyText}</div>
          </div>
          <div className="sampleSubmissionConfirmDel__divider" role="separator" aria-hidden />
          <div className="sampleSubmissionConfirmDel__IssueSuccess">
            <CheckCircleFilled aria-hidden />
            <p>
              Great! You have chosen to send a replacement sample. Please let us know when you plan to send it.
            </p>
          </div>
          <div className="sampleSubmissionConfirmDel__commentLabel">Add Comment</div>
          <div className="sampleSubmissionConfirmDel__contentBox">
            <Comment
              commentRef={commentRef}
              defaultValue={commentDefaultValue}
              placeholder="Enter a description..."
              textareaClassName="SubmissionFrom__Comment_Add--confirmModalBox"
              variant="borderless"
            />
          </div>
          {renderScheduleDates()}
        </>
      );
    }

    if (submissionComment) {
      return (
        <>
          <div className="sampleSubmissionConfirmDel__addHeader">
            <div className="sampleSubmissionConfirmDelTitle sampleSubmissionConfirmDelTitle--add">{headerText}</div>
            <div className="sampleSubmissionConfirmDelSubtitle">{bodyText}</div>
          </div>
          <div className="sampleSubmissionConfirmDel__divider" role="separator" aria-hidden />
          <div className="sampleSubmissionConfirmDel__contentBox">
            <Comment
              commentRef={commentRef}
              defaultValue={commentDefaultValue}
              placeholder="Enter a description..."
              textareaClassName="SubmissionFrom__Comment_Add--confirmModalBox"
              variant="borderless"
            />
          </div>
        </>
      );
    }

    return (
      <>
        <div className={isDeleteHeader ? "sampleSubmissionDelTitle" : "sampleSubmissionConfirmDelTitle"}>{headerText}</div>
        <div className="sampleSubmissionConfirmDelTitleMessage">{bodyText}</div>
      </>
    );
  };

  return (
    <Modal
      title=""
      open={open}
      maskClosable={false}
      closable={showCloseIcon}
      closeIcon={showCloseIcon ? <CloseOutlined /> : null}
      onCancel={showCloseIcon ? handleCancel : undefined}
      centered
      destroyOnClose
      footer={null}
      width={modalWidth}
      wrapClassName={`SampleSubmission__ConfirmModal ${(submissionComment || isIssueScheduleVariant) ? "SampleSubmission__ConfirmModal_Add" : ""}`}
    >
      <div className={`sampleSubmissionConfirmDel ${(submissionComment || isIssueScheduleVariant) ? "sampleSubmissionConfirmDel--add" : ""}`}>
        {renderModalBody()}
        {submissionComment && !thirdParty && !isLoggedOut && renderScheduleDates()}
        {isNoChangesConfirmation && (
          <>
            <div className="SampleSubmissionConfirmModal__Divider" />
            <p className="sampleSubmissionConfirmDelTitleMessage sampleSubmissionConfirmDelTitleMessage--noChanges">
              No changes to be submitted
            </p>
          </>
        )}
        {editedSamplePayload && !isNoChangesConfirmation && (
          <SampleSubmissionModalEditsInfo
            editedSamplePayload={editedSamplePayload}
            sampleFields={sampleFields}
            sampleFieldsInfo={sampleFieldsInfo}
            sampleIdFields={sampleIdFields}
          />
        )}
        {/* <div
          className="sampleSubmissionConfirmDel__divider sampleSubmissionConfirmDel__divider--footer"
          role="separator"
          aria-hidden
        /> */}
        <div className="SampleSubmissionConfirmModal__Divider" />
        <div className="sampleSubmissionConfirmDelBtn">
          <Button
            type="default"
            onClick={handleCancel}
          >
            {buttonText[0]}
          </Button>
          <Button
            type="primary"
            onClick={handlePrimaryClick}
            loading={loading}
            disabled={isIssueScheduleVariant && !selectedScheduleDateKey}
          >
            {primaryBtnLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
